import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { buySubscription, buyCredits, isNativeAndroid } from "../lib/billing";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Crown, Coins, MessageCircleHeart, Smartphone } from "lucide-react";

const PLAN_LABELS = {
  admin: "Amministratore — illimitato",
  sub: "Abbonamento attivo",
  gift: "Giorni regalati",
  trial: "Prova gratuita",
  none: "Nessun piano attivo",
};

export default function Piani() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [busy, setBusy] = useState("");
  const [content, setContent] = useState(null);
  const native = isNativeAndroid();

  useEffect(() => {
    if (user) api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const buy = async (kind) => {
    if (!user) {
      navigate("/accedi?redirect=/piani");
      return;
    }
    setBusy(kind);
    try {
      const data = kind === "subscription" ? await buySubscription() : await buyCredits();
      setEnt(data);
      toast.success(
        kind === "subscription"
          ? data.simulated ? "Abbonamento attivato (modalità sviluppo)." : "Abbonamento attivato. Benvenuta tra le stelle."
          : data.simulated ? "3 consultazioni aggiunte (modalità sviluppo)." : "3 consultazioni aggiunte al tuo account."
      );
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-14 sm:py-20" data-testid="piani-page">
      <div className="text-center mb-10">
        <p className="eyebrow">Piani</p>
        <h1 className="font-display text-4xl sm:text-5xl gold-text mt-3">
          {native ? "Scegli come consultare" : "Come consultare"}
        </h1>
        <p className="text-purple-200/70 mt-4 text-sm max-w-2xl mx-auto leading-relaxed">
          {content?.piani_intro || "Ogni consultazione include sempre 2 approfondimenti con Arcana, la tua intelligenza di lettura: un confronto interattivo su ciò che è emerso. Non sono una seconda consultazione e non si acquistano separatamente."}
        </p>
      </div>

      <div className="mystic-card p-6 sm:p-8 mb-10" data-testid="piani-arcana-info">
        <p className="eyebrow mb-3">Come funziona Arcana</p>
        <div className="text-sm text-purple-100/80 leading-relaxed space-y-3">
          <p>
            Arcana non è un'intelligenza artificiale generica: è istruita sulla metodologia di lettura e sui significati
            della Vera Sibilla che Virginia ha maturato in oltre trent'anni di cartomanzia. Interpreta le carte seguendo
            il suo metodo: la domanda, le carte estratte, le loro posizioni, il rapporto tra le carte, le sfumature
            della stesa e il contesto della domanda.
          </p>
          <p>
            Arcana non è Virginia e non la sostituisce:{" "}
            <span className="text-amber-200">Virginia è il consulto personale con lei; Arcana è il suo metodo di lettura, disponibile nell'app.</span>
          </p>
        </div>
      </div>

      {user && ent && (
        <div className="mystic-card p-5 mb-10 text-center" data-testid="piani-status">
          <p className="text-xs text-purple-300/60">
            Il tuo piano: <span className="text-amber-200 font-medium">{PLAN_LABELS[ent.plan]}</span>
            {ent.consults_left_today !== null && ent.plan !== "none" && (
              <> · oggi ancora <span className="text-amber-200">{ent.consults_left_today}</span> consultazioni</>
            )}
            {ent.credits > 0 && <> · crediti: <span className="text-amber-200">{ent.credits}</span></>}
            {ent.plan === "trial" && ent.trial_ends_at && (
              <> · prova fino al {new Date(ent.trial_ends_at).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}</>
            )}
            {(ent.plan === "sub" || ent.plan === "gift") && ent.access_until && (
              <> · {ent.plan === "sub" ? "rinnovo" : "accesso"} fino al {new Date(ent.access_until).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}</>
            )}
          </p>
        </div>
      )}

      <div className={`grid grid-cols-1 ${native ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
        <div className="mystic-card p-7 flex flex-col" data-testid="piani-trial">
          <Sparkles className="w-6 h-6 text-amber-400 mb-4" />
          <h2 className="font-display text-2xl text-amber-100">Prova gratuita</h2>
          <p className="text-3xl text-amber-300 font-display mt-2">3 giorni</p>
          <ul className="text-sm text-purple-200/75 mt-5 space-y-2 flex-1 leading-relaxed">
            <li>1 consultazione al giorno</li>
            <li>+ 2 approfondimenti per ogni consultazione</li>
          </ul>
          <p className="text-[11px] text-purple-300/50 mt-5">La prova inizia automaticamente alla tua prima consultazione.</p>
        </div>

        {native ? (
          <>
            <div className="mystic-card p-7 flex flex-col border-amber-500/50 relative" data-testid="piani-sub">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] bg-amber-500 text-[#1c0d31] font-semibold rounded-full px-4 py-1">
                Consigliato
              </span>
              <Crown className="w-6 h-6 text-amber-400 mb-4" />
              <h2 className="font-display text-2xl text-amber-100">Abbonamento</h2>
              <p className="text-3xl text-amber-300 font-display mt-2">€ 9,99<span className="text-sm text-purple-300/60">/mese</span></p>
              <ul className="text-sm text-purple-200/75 mt-5 space-y-2 flex-1 leading-relaxed">
                <li>5 consultazioni al giorno</li>
                <li>+ 2 approfondimenti per ogni consultazione</li>
                <li>+ Hai già tirato le carte dal tuo mazzo? Arcana le interpreta per te: inserisci la tua stesa e ricevi un responso completo, con la stessa cura di sempre</li>
                <li>+ Verdetto Settimanale: una lettura delle carte dedicata alla tua settimana</li>
                <li>+ Oroscopo settimanale personalizzato: ogni lunedì, calcolato sul tuo tema natale e sui transiti astrologici reali</li>
                <li>+ Numerologia quotidiana personalizzata: ogni mattina la previsione del tuo numero del giorno, con notifica</li>
                <li>+ Accesso a tutte le funzionalità astrologiche e numerologiche riservate agli abbonati</li>
                <li>+ Curiosità e contenuti dedicati alla Luna, direttamente nelle notifiche</li>
                <li>+ Crediti regalo a Natale e per il tuo compleanno</li>
                <li>+ 1 domanda personale a Virginia ogni mese, con risposta diretta da lei</li>
                <li>Possibilità di 5 € di sconto sul consulto privato, da concordare con Virginia</li>
              </ul>
              <button
                onClick={() => buy("subscription")}
                disabled={busy !== ""}
                data-testid="piani-subscribe-btn"
                className="gold-btn w-full py-3 text-sm mt-6 disabled:opacity-60"
              >
                {busy === "subscription" ? "Attivazione…" : "Attiva l'abbonamento"}
              </button>
            </div>

            <div className="mystic-card p-7 flex flex-col" data-testid="piani-credits">
              <Coins className="w-6 h-6 text-amber-400 mb-4" />
              <h2 className="font-display text-2xl text-amber-100">Acquisto singolo</h2>
              <p className="text-3xl text-amber-300 font-display mt-2">€ 2,99</p>
              <ul className="text-sm text-purple-200/75 mt-5 space-y-2 flex-1 leading-relaxed">
                <li>3 consultazioni</li>
                <li>+ 2 approfondimenti per ogni consultazione</li>
              </ul>
              <button
                onClick={() => buy("credits")}
                disabled={busy !== ""}
                data-testid="piani-credits-btn"
                className="w-full py-3 text-sm mt-6 rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-all disabled:opacity-60"
              >
                {busy === "credits" ? "Acquisto…" : "Acquista 3 consultazioni"}
              </button>
            </div>
          </>
        ) : (
          <div className="mystic-card p-7 flex flex-col border-amber-500/50" data-testid="piani-app-info">
            <Smartphone className="w-6 h-6 text-amber-400 mb-4" />
            <h2 className="font-display text-2xl text-amber-100">Acquisti e abbonamenti dall'app</h2>
            <div className="text-sm text-purple-200/75 mt-5 space-y-3 flex-1 leading-relaxed">
              <p>
                Sul sito usi le consultazioni e i crediti che hai già: restano tutti disponibili,
                insieme al tuo storico e ai tuoi contenuti.
              </p>
              <p>
                Per attivare l'abbonamento o acquistare nuove consultazioni serve l'app Android di Arcana,
                scaricabile da Google Play: gli acquisti avvengono solo nell'app, in modo sicuro con il tuo account Google.
              </p>
              <div className="pt-4 border-t border-amber-500/15" data-testid="piani-web-benefits">
                <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400/70 mb-3">L'abbonamento include anche</p>
                <ul className="space-y-2">
                  <li>Oroscopo settimanale personalizzato, sul tuo tema natale e sui transiti astrologici reali</li>
                  <li>Numerologia quotidiana personalizzata, con la previsione del tuo numero del giorno</li>
                  <li>Accesso a tutte le funzionalità astrologiche e numerologiche riservate agli abbonati</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mystic-card p-6 mt-10 flex items-start gap-4" data-testid="piani-virginia-note">
        <MessageCircleHeart className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-purple-200/70 leading-relaxed">
          Arcana è un confronto interattivo su ciò che emerge dalle tue consultazioni e non sostituisce il consulto personale. Per un'esperienza davvero su misura, il consulto privato con
          Virginia resta un percorso a sé — e con l'abbonamento hai ogni mese una domanda personale con risposta
          diretta da lei.
        </p>
      </div>

      {!user && (
        <p className="text-center text-sm text-purple-200/70 mt-6">
          <Link to="/accedi?redirect=/piani" className="text-amber-300 hover:text-amber-200 underline underline-offset-2">Accedi o registrati</Link> per iniziare la prova gratuita.
        </p>
      )}
    </div>
  );
}
